/**
 * Guides content for Mu Beginning (Season 6).
 * Values taken from Server/MuServer Data configs.
 * Item icons served from /guides/items/*.webp
 */

const I = (file) => `/guides/items/${file}`;

const guidesContent = [
  {
    id: "basic",
    title: "Basic information",
    subtitle: "SERVER BASICS",
    topics: [
      {
        id: "welcome",
        title: "Welcome to Mu Beginning",
        paragraphs: [
          "Mu Beginning is a Season 6 Episode 3 private server with classic MU gameplay, custom systems and quality-of-life features.",
          "Use this Guides section to learn classes, resets, VIP, dungeons, Fenrir crafting, events and more — all values match our live GameServer data.",
          "Join Discord for announcements, support and community help.",
        ],
      },
      {
        id: "game-options",
        title: "Game Options (Game Settings)",
        paragraphs: [
          "Open Game Settings from the in-game System / Escape menu (Option / Game Settings). The window title is “Game Settings”.",
          "Settings are saved locally under Data\\Custom\\GameOption\\GameOption.xml so they keep after you restart the client.",
          "There are two main tabs: Game and Graphics. Use them to control combat helpers, UI display, performance, resolution, FPS, sound and what is rendered on screen.",
          "Tip: if the game feels laggy in crowded maps or events, lower Graphics Mode, reduce FPS, or enable Hide options (players/monsters/wings) to improve FPS.",
        ],
      },
      {
        id: "game-options-game-tab",
        title: "Game Tab — Controls & Display",
        paragraphs: [
          "Drop Item / Show Name Item — choose “Always Show item Names” or “Do Not Show”. When always-show is on, ground item names stay visible without hovering each drop.",
          "Auto HP — Turn On / Turn Off. When enabled, the client requests automatic HP recovery behavior from the server (useful while hunting).",
          "Auto Attack — Turn On / Turn Off. Enables automatic attack / auto-PK style combat assist (Auto Attack). Use carefully and follow server fair-play rules.",
          "Show HP Bar — Always Show or Do Not Show. Controls whether HP bars are drawn above characters/monsters.",
          "Show Character Name — Always Show or Do Not Show. Controls whether character names stay visible on screen.",
          "VSync — locks the frame presentation to your monitor refresh rate to reduce screen tearing (may lower FPS on weak PCs).",
          "Effect 15 — toggles an extra visual effect layer used by the client (can be turned off for performance).",
          "Screen Camera — toggles the screen/camera helper mode used by our custom camera option.",
        ],
      },
      {
        id: "game-options-graphics-tab",
        title: "Graphics Tab — Performance & Sound",
        paragraphs: [
          "Mode Graphics — presets: Low, Medium, High, Ultra. Changing the preset adjusts shadows, glow, excellent effects and related quality flags automatically.",
          "FPS Limit — choose 60 / 120 / 144 / 175 / 200 / Unlimited. Higher FPS feels smoother but needs a stronger PC. Unlimited removes the soft cap.",
          "Option Game (render toggles) — Hide Wings, Hide Weapon, Hide Texture, Hide Monster, Hide Terrain, Hide Player. Use these to simplify what is drawn and raise FPS in war/events.",
          "On/Off Music — enable or disable background music. Music Volume slider (0–100) controls loudness when music is on.",
          "On/Off World Sound — enable or disable world / environment sounds (monster hits, map ambience, etc.).",
          "Resolution — pick your screen resolution from the list (scroll if needed). The client applies the new size and refreshes UI layout.",
          "Font Size — Font Size 13 / 14 / 15. Larger fonts help readability on high-resolution monitors.",
          "Recommended: start with High + 60/120 FPS; if you drop frames in Castle Siege or invasions, switch to Medium/Low and hide other players/monsters.",
        ],
      },
      {
        id: "helper",
        title: "MU Helper",
        paragraphs: [
          "The MU Helper assists with auto-hunt, potions and pick-up settings.",
          "Configure it carefully and follow fair-play rules — abuse may result in penalties.",
          "Game Options (Auto Attack / Auto HP) work together with Helper settings — review both so your character behaves as you expect.",
        ],
      },
      {
        id: "interface",
        title: "Game Interface",
        paragraphs: [
          "Use inventory, warehouse, party, quest and Event Time panels to play efficiently.",
          "Open Event Time in-game to see live countdowns for Blood Castle, invasions and more — the website Upcoming Events panel uses the same schedules.",
          "Voice chat push-to-talk is F1 (see Systems → Voice Chat). Game Settings handles graphics/sound/UI; hotkeys like F1 are separate.",
        ],
      },
    ],
  },

  {
    id: "classes",
    title: "Classes",
    subtitle: "CHARACTER BUILDS",
    topics: [
      {
        id: "dw",
        title: "Dark Wizard (DW)",
        paragraphs: [
          "Ranged magic class. Primary damage stat is Energy; Vitality for survivability.",
          "Strong in PvE waves and party content with AoE skills. Focus positioning and mana management.",
          "Progression path includes Soul Master and Grand Master evolutions (Season 6).",
        ],
      },
      {
        id: "dk",
        title: "Dark Knight (DK)",
        paragraphs: [
          "Melee front-line fighter. Strength for damage, Agility for attack/defense speed, Vitality for HP.",
          "Excellent for PvP and tanking events such as Blood Castle and Chaos Castle.",
          "Evolves through Blade Knight and Blade Master.",
        ],
      },
      {
        id: "fe",
        title: "Fairy Elf / Muse Elf (FE)",
        paragraphs: [
          "Hybrid support and ranged DPS. Agility builds for bow damage; Energy for healing/buff utility.",
          "Highly valued in parties for buffs and sustain. Strong in PvE and Castle Siege support roles.",
          "Evolves through Muse Elf and High Elf.",
        ],
      },
      {
        id: "mg",
        title: "Magic Gladiator (MG)",
        paragraphs: [
          "Hybrid warrior-mage. Can use swords and magic; often Strength/Energy hybrids.",
          "Flexible for both close combat and ranged spells. Strong all-rounder for events and farming.",
          "Does not use the same 2nd/3rd quest path as starter classes — check Quest guides in-game.",
        ],
      },
      {
        id: "dl",
        title: "Dark Lord (DL)",
        paragraphs: [
          "Command-based class with pets and party leadership skills. Leadership (Command) is a key stat.",
          "Strong in PvP and large-scale fights. Uses Dark Horse / Dark Raven and special DL sets.",
          "Requires proper Leadership investment for skill effectiveness.",
        ],
      },
      {
        id: "su",
        title: "Summoner (SU)",
        paragraphs: [
          "Magic summoner class with curses, books and pet summons. Energy is the main damage stat.",
          "Excels at mid/long range control and PvE. Learn book skills and summon management early.",
          "Evolves through Bloody Summoner and Dimension Master.",
        ],
      },
      {
        id: "rf",
        title: "Rage Fighter (RF)",
        paragraphs: [
          "Close-range martial artist with gloves and special RF skills. Strength and Vitality are core.",
          "High burst and mobility in PvP; strong farming when geared.",
          "Evolves through Fist Master. No Grow Lancer (GL) on this server.",
        ],
      },
    ],
  },

  {
    id: "systems",
    title: "Systems",
    subtitle: "SERVER FEATURES",
    topics: [
      {
        id: "battle-pass",
        title: "Battle Pass",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "dungeons",
        title: "Dungeon System",
        // Full EN/HE content lives in guideSpecialContent.js (CustomMix.xml recipes).
        paragraphs: [],
      },
      {
        id: "vip",
        title: "VIP System",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "fenrir-mix",
        title: "Fenrir Mix",
        // Full EN/HE visual mix recipes live in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "reset",
        title: "Reset System",
        // Full EN/HE content + per-reset table lives in guideSpecialContent.js (ResetTable.txt).
        paragraphs: [],
      },
      {
        id: "grand-reset",
        title: "Grand Reset",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "voice",
        title: "Voice Chat",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "quests",
        title: "Quest Systems",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "market",
        title: "Market System",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "expanded-inventory",
        title: "Expanded Inventory",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
      {
        id: "online-bonus",
        title: "Online Bonus",
        // Full EN/HE content lives in guideSystemsContent.js
        paragraphs: [],
      },
    ],
  },

  {
    id: "leveling",
    title: "Leveling & Progression",
    subtitle: "GROW FASTER",
    topics: [
      {
        id: "spots",
        title: "Training Spots",
        paragraphs: [
          "Start in Lorencia / Noria / Devias starter maps, then move to higher maps as level and gear improve.",
          "Use party hunting, Online Bonus, VIP EXP and dungeons to speed early resets.",
          "Kanturu Dungeon is important later for Fenrir materials; Chaos/Devil Square dungeons help jewel income.",
        ],
      },
      {
        id: "progression",
        title: "Recommended Progression",
        paragraphs: [
          "1) Reach level 400 and start resets · 2) Unlock VIP if you play daily · 3) Use dungeons for EXP/jewels · 4) Climb to 50 resets · 5) Grand Reset for a large point boost · 6) Craft Fenrir when materials are ready.",
          "Check Commands and Event Time regularly so you never miss free reward windows.",
        ],
      },
    ],
  },

  {
    id: "wings",
    title: "Wings",
    subtitle: "CHAOS MACHINE",
    topics: [
      { id: "wings-1", title: "Level 1 Wings", paragraphs: [] },
      { id: "wings-2", title: "Level 2 Wings", paragraphs: [] },
      { id: "wings-3", title: "Level 3 Wings", paragraphs: [] },
    ],
  },

  {
    id: "class-change",
    title: "Class Change",
    subtitle: "QUEST WALKTHROUGH",
    topics: [
      { id: "class-1", title: "First Class Change", paragraphs: [] },
      { id: "class-2", title: "Second Class Change", paragraphs: [] },
      { id: "class-3", title: "Third Class Change", paragraphs: [] },
    ],
  },

  {
    id: "events",
    title: "Events",
    subtitle: "TIMED CONTENT",
    // Full EN/HE content lives in guideEventsContent.js (via guideSpecialContent).
    topics: [
      { id: "blood-castle", title: "Blood Castle", paragraphs: [] },
      { id: "devil-square", title: "Devil Square", paragraphs: [] },
      { id: "chaos-castle", title: "Chaos Castle", paragraphs: [] },
      { id: "illusion", title: "Illusion Temple", paragraphs: [] },
      { id: "imperial", title: "Imperial Guardian", paragraphs: [] },
      { id: "kanturu", title: "Kanturu Event", paragraphs: [] },
      { id: "kalima", title: "Kalima Kundun", paragraphs: [] },
      { id: "invasions", title: "World Invasions", paragraphs: [] },
      { id: "boss-together", title: "Boss Battle Together", paragraphs: [] },
      { id: "arka-war", title: "Arka War", paragraphs: [] },
      { id: "double-goer", title: "Double Goer", paragraphs: [] },
      { id: "castle-siege", title: "Castle Siege", paragraphs: [] },
      { id: "crywolf", title: "Crywolf", paragraphs: [] },
      { id: "castle-deep", title: "Castle Deep", paragraphs: [] },
      { id: "moss-merchant", title: "Moss Merchant", paragraphs: [] },
      { id: "tvt", title: "TvT", paragraphs: [] },
      { id: "other-events", title: "Other Events", paragraphs: [] },
    ],
  },
];

export default guidesContent;
